import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { useArenaStore } from '../../../store/useArenaStore';
import { GameHudProps } from '../../../presentation/GamePresentationTypes';

import { useTableLayout } from '../../../components/Table/layout/TableLayoutContext';

export interface WinningContractSummary {
  key: string;
  winnerName: string;
  bid: number;
  direction: string | null;
  dirText: string;
  dirArrow: string;
  suitText: string;
  suitSymbol: string;
  isRedSuit: boolean;
  isNoTrump: boolean;
  suitColor: string;
  label: string;
}

function clampVmin(minPx: number, vminVal: number, maxPx: number): string {
  return `clamp(${minPx}px, ${vminVal}vmin, ${maxPx}px)`;
}

export const BidWhistTableHud: React.FC<GameHudProps> = ({
  winningContractSummary,
  isDelayingWinningContract,
  booksNeededToSet = null,
  booksToSetData = null,
  showBooksToSet = false,
  tableColor = 'default',
  config: propConfig,
}) => {
  const storeConfig = useArenaStore((s) => s.gameConfig);
  const activeConfig = propConfig ?? storeConfig;
  const { dimensions, containerRef } = useTableLayout();

  // Track and cache last valid booksNeededToSet per contract to avoid flickers/disappearing during network state updates
  const lastValidBooksRef = React.useRef<number | null>(null);
  const currentIncomingBooks = booksToSetData?.booksNeededToSet ?? booksNeededToSet;

  const contractKey = winningContractSummary
    ? `${winningContractSummary.key || ''}_${winningContractSummary.bid}_${winningContractSummary.label || ''}`
    : null;

  const prevContractKeyRef = React.useRef<string | null>(null);
  const defaultBooksRef = React.useRef<number | null>(null);
  const prevBooksRef = React.useRef<number | null>(null);
  const [isAlertAnimating, setIsAlertAnimating] = React.useState<boolean>(false);
  const [alertKey, setAlertKey] = React.useState<number>(0);
  const alertTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // If contract changes or is absent, reset cached books
  if (contractKey !== prevContractKeyRef.current || !contractKey) {
    prevContractKeyRef.current = contractKey;
    lastValidBooksRef.current = null;
    defaultBooksRef.current = null;
    prevBooksRef.current = null;
  }

  if (contractKey && currentIncomingBooks !== null && currentIncomingBooks !== undefined) {
    lastValidBooksRef.current = currentIncomingBooks;
  }

  const effectiveBooksNeeded = lastValidBooksRef.current;
  const isDisplayingBooksToSet =
    (activeConfig?.ui?.showBooksToSetCounter ?? true) &&
    showBooksToSet &&
    effectiveBooksNeeded !== null &&
    effectiveBooksNeeded !== undefined;

  React.useEffect(() => {
    if (!contractKey || effectiveBooksNeeded === null || effectiveBooksNeeded === undefined) {
      setIsAlertAnimating(false);
      return;
    }

    if (defaultBooksRef.current === null) {
      defaultBooksRef.current = effectiveBooksNeeded;
      prevBooksRef.current = effectiveBooksNeeded;
      setIsAlertAnimating(false);
      return;
    }

    // Only alert when books needed actually changes while playing the same contract
    if (prevBooksRef.current !== null && effectiveBooksNeeded !== prevBooksRef.current) {
      prevBooksRef.current = effectiveBooksNeeded;
      setIsAlertAnimating(true);
      setAlertKey((prev) => prev + 1);

      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }
      alertTimerRef.current = setTimeout(() => {
        setIsAlertAnimating(false);
        alertTimerRef.current = null;
      }, 450);
    }
  }, [contractKey, effectiveBooksNeeded]);

  React.useEffect(() => {
    return () => {
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }
    };
  }, []);

  const colorStyles = React.useMemo(() => {
    switch (tableColor) {
      case 'blue':
        return {
          border: 'border-sky-300/30',
          separator: 'border-sky-300/20',
          primaryText: 'text-sky-200/90',
          secondaryText: 'text-sky-200/90',
        };
      case 'red':
        return {
          border: 'border-rose-300/30',
          separator: 'border-rose-300/20',
          primaryText: 'text-rose-200/90',
          secondaryText: 'text-rose-200/90',
        };
      case 'pink':
        return {
          border: 'border-pink-300/30',
          separator: 'border-pink-300/20',
          primaryText: 'text-pink-200/90',
          secondaryText: 'text-pink-200/90',
        };
      case 'gold':
        return {
          border: 'border-amber-300/30',
          separator: 'border-amber-300/20',
          primaryText: 'text-amber-200/90',
          secondaryText: 'text-amber-200/90',
        };
      case 'default':
      default:
        return {
          border: 'border-emerald-300/30',
          separator: 'border-emerald-300/20',
          primaryText: 'text-emerald-200/90',
          secondaryText: 'text-emerald-200/90',
        };
    }
  }, [tableColor]);

  // Scaled compact font sizes and arrow size matching TableBranding felt cloth depth
  const fontSize = {
    base: clampVmin(8.5, 1.9, 12),
    books: clampVmin(8, 1.7, 11),
  } as const;

  const arrowSize = 10;

  // Calculate vertical clearance between top of page (0px) and top edge of table
  const containerHeight =
    containerRef?.current?.clientHeight ||
    (typeof window !== 'undefined' ? window.innerHeight : 800);
  const tableCenterY = containerHeight / 2 - (dimensions?.verticalOffset ?? 0);
  const tableTopY = Math.max(0, tableCenterY - (dimensions?.height ?? 600) / 2);
  const headerAreaHeight = tableTopY > 0 ? tableTopY : 64;

  return (
    <>
      {/* Bid Whist Header: Hidden per user request */}
      <span data-testid="bid-whist-game-header" className="sr-only">
        BID WHIST
      </span>

      {/* Winning Bid HUD: Luxury Plaque with SVG border and modal tint */}
      <AnimatePresence>
        {winningContractSummary && !isDelayingWinningContract && (
          <motion.div
            initial={{ opacity: 0, x: -12, y: -6, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: -12, y: -6, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed top-2.5 left-4 sm:top-3.5 sm:left-5 z-[60] pointer-events-none select-none"
          >
            <div
              data-testid="bid-whist-hud-container"
              className="relative h-[45px] sm:h-[53px] rounded-xl sm:rounded-2xl px-3 sm:px-3.5 py-1.5 sm:py-2 flex items-center gap-3 sm:gap-3.5 bg-gradient-to-b from-[#0e1728] via-[#09101d] to-[#050912] shadow-[0_12px_36px_rgba(0,0,0,0.85),0_4px_16px_rgba(0,0,0,0.6),0_0_10px_1px_rgba(229,142,25,0.35)]"
            >
              {/* Continuous SVG Border running around the entire plaque perimeter */}
              <svg
                data-testid="bid-whist-hud-svg-border"
                className="absolute inset-0 w-full h-full pointer-events-none overflow-visible rounded-xl sm:rounded-2xl z-20"
              >
                <defs>
                  <linearGradient id="hudBorderGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#fef08a" />
                    <stop offset="50%" stopColor="#d48b28" />
                    <stop offset="100%" stopColor="#92400e" />
                  </linearGradient>
                </defs>
                <rect
                  x="0.75"
                  y="0.75"
                  width="calc(100% - 1.5px)"
                  height="calc(100% - 1.5px)"
                  rx="14"
                  ry="14"
                  fill="none"
                  stroke="url(#hudBorderGoldGrad)"
                  strokeWidth="1.5"
                />
              </svg>

              {/* Subtle top edge inner highlight reflection */}
              <div className="absolute inset-x-2 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none z-10" />

              {/* Winning Bid Column */}
              <div className="flex items-center gap-2 relative z-10">
                {/* Winning Bid Number (-10% scaled) - Radiant Gold */}
                <div className="flex items-baseline">
                  <span className="text-[27px] sm:text-[31px] font-black text-amber-300 font-mono leading-none tracking-tight drop-shadow-[0_2px_8px_rgba(245,158,11,0.4)]">
                    {winningContractSummary.bid}
                  </span>
                </div>

                {/* Trump & Direction Column */}
                <div className="flex flex-col items-start justify-center">
                  {/* Direction with Arrow and Text */}
                  {winningContractSummary.direction && (
                    <div className="flex items-center gap-1 leading-none text-white">
                      {(winningContractSummary.direction || '').toUpperCase() === 'DOWN' ? (
                        <ArrowDown size={13} strokeWidth={3} className="text-emerald-400 shrink-0 drop-shadow-[0_0_6px_rgba(52,211,153,0.5)]" />
                      ) : (
                        <ArrowUp size={13} strokeWidth={3} className="text-rose-400 shrink-0 drop-shadow-[0_0_6px_rgba(251,113,133,0.5)]" />
                      )}
                      <span className="text-[12px] sm:text-[13px] font-black font-mono uppercase tracking-wider text-white">
                        {winningContractSummary.dirText || winningContractSummary.direction}
                      </span>
                    </div>
                  )}

                  {/* Trump / NT */}
                  <div className="flex items-center gap-1 leading-none mt-0.5">
                    {winningContractSummary.isNoTrump ? (
                      <>
                        <span className="text-[12px] sm:text-[13.5px] font-black text-amber-300 font-mono tracking-wider drop-shadow-sm">
                          NO TRUMP
                        </span>
                        <span className="sr-only">NT</span>
                      </>
                    ) : (
                      <div className="flex items-center gap-1">
                        {winningContractSummary.suitSymbol && (
                          <span
                            className={`text-[15px] sm:text-[17px] leading-none font-bold select-none font-serif ${
                              winningContractSummary.isRedSuit
                                ? 'text-rose-400 drop-shadow-[0_1px_3px_rgba(244,63,94,0.5)]'
                                : 'text-white drop-shadow-[0_1px_3px_rgba(255,255,255,0.4)]'
                            }`}
                          >
                            {winningContractSummary.suitSymbol}
                          </span>
                        )}
                        {winningContractSummary.suitText && (
                          <span
                            className={`text-[12px] sm:text-[13px] font-black uppercase tracking-wider font-mono ${
                              winningContractSummary.isRedSuit ? 'text-rose-200' : 'text-slate-200'
                            }`}
                          >
                            {winningContractSummary.suitText}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Side-by-Side Books To Set (Vertical Separator + Big Number + 2-Row Column) */}
              {isDisplayingBooksToSet && (
                <>
                  <div
                    data-testid="books-to-set-separator"
                    className="w-[1.5px] self-stretch bg-gradient-to-b from-amber-400/20 via-amber-400/70 to-amber-400/20 my-1 rounded-full shadow-[0_0_4px_rgba(245,158,11,0.4)] relative z-10"
                  />
                  <div
                    data-testid="books-to-set-text"
                    data-alert-animating={isAlertAnimating ? 'true' : undefined}
                    className="flex items-center gap-1.5 leading-none drop-shadow-sm relative z-10"
                  >
                    {/* Big Number (-10% scaled) - Contrasting Coral Rose for "To Set" danger/target threshold */}
                    <motion.span
                      key={`books-pop-${contractKey}-${effectiveBooksNeeded}-${alertKey}`}
                      animate={
                        isAlertAnimating
                          ? { scale: [1, 1.25, 1], color: '#ffe4e6' }
                          : { scale: 1, color: '#fb7185' }
                      }
                      transition={{
                        duration: isAlertAnimating ? 0.45 : 0.2,
                        times: isAlertAnimating ? [0, 0.35, 1] : undefined,
                        ease: 'easeOut',
                      }}
                      className="text-[27px] sm:text-[31px] font-black font-mono leading-none tracking-tight text-rose-400 select-none drop-shadow-[0_2px_8px_rgba(244,63,94,0.4)]"
                    >
                      {effectiveBooksNeeded}
                    </motion.span>

                    {/* 2-Row Column for TO and SET */}
                    <div className="flex flex-col items-start justify-center leading-tight">
                      <span className="text-[10.5px] sm:text-[11.5px] font-black font-mono uppercase tracking-wider text-amber-200/90 leading-none">
                        TO
                      </span>
                      <span className="text-[10.5px] sm:text-[11.5px] font-black font-mono uppercase tracking-wider text-amber-200/90 leading-none mt-0.5">
                        SET
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
